@if (showSkeleton()) {
  <app-menu-skeleton />
} @else if (menu.isNotFound()) {
  <app-not-found />
} @else if (menu.isError()) {
  <div
    #menuErrorRef
    role="alert"
    tabindex="-1"
    class="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center text-slate-900 outline-none"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="h-16 w-16 opacity-60"
      aria-hidden="true"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-2Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
    <h1 class="text-2xl font-semibold">{{ 'menu.error' | translate }}</h1>
    <button
      type="button"
      class="rounded-full border border-slate-200 bg-white px-6 py-2.5 font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
      (click)="menu.retry()"
    >
      {{ 'common.retry' | translate }}
    </button>
  </div>
} @else if (menu.loaded(); as data) {
  <div class="qm-page min-h-screen bg-slate-50 pb-12 font-sans text-slate-900">
    <!-- Overlays are native modal dialogs — everything outside them is inert while one is open. -->
    <div>
      <app-restaurant-header
        [restaurant]="data.restaurant"
        [showInfo]="infoSheetReady()"
        [tableName]="table.tableName()"
        (openInfo)="openInfo($event)"
      />

      <app-banner-carousel />

      <app-category-tabs
        [tabs]="menu.tabs()"
        [selected]="menu.selectedCategoryId()"
        (selectTab)="onSelectCategory($event)"
      />

      <!-- Search scrolls away with the content; only the category tabs stay pinned on scroll. -->
      <app-menu-search
        [query]="menu.query()"
        [announce]="menu.isSearching() ? menu.countLabel() : ''"
        (queryChange)="menu.setQuery($event)"
      />

      <main class="qm-main mx-auto max-w-md px-6 pt-6">
        <div class="qm-section-head mb-6 flex items-center justify-between">
          <div class="flex flex-col">
            <h2 class="qm-heading text-2xl leading-none font-bold">{{ menu.displayHeading() }}</h2>
            <span
              class="qm-count mt-2 text-[10px] font-bold tracking-widest text-slate-500 uppercase"
              [attr.aria-hidden]="menu.isSearching() ? 'true' : null"
            >
              {{ menu.countLabel() }}
            </span>
          </div>

          <app-layout-toggle [layout]="menu.layout()" (toggleLayout)="menu.toggleLayout()" />
        </div>

        <div [class]="gridClass()">
          @for (entry of menu.displayedItems(); track entry.item.id) {
            <app-menu-item-card
              [item]="entry.item"
              [nameRanges]="entry.nameRanges"
              [descriptionRanges]="entry.descriptionRanges"
              [layout]="menu.layout()"
              [ordering]="canAddToCart()"
              [detailsEnabled]="itemSheetReady()"
              (openImage)="openImage($event)"
              (openDetails)="openItemDetails(entry.item, $event)"
            />
          } @empty {
            <div class="qm-empty col-span-full py-20 text-center" role="status">
              <p class="text-slate-500">
                {{ (menu.isSearching() ? 'menu.emptySearch' : 'menu.emptyCategory') | translate }}
              </p>
            </div>
          }
        </div>
      </main>

      @if (showBar()) {
        <app-action-bar
          [showWaiter]="waiterReady()"
          [showBill]="menu.showBill() && billReady()"
          [showCart]="cartVisible()"
          [cartCount]="cart.count()"
          [cartTotal]="cart.total()"
          [waiterDisabled]="table.waiterCooldown() || table.waiterChecking()"
          (callWaiter)="onCallWaiter($event)"
          (openBill)="openBill($event)"
          (openCart)="openCart($event)"
        />
      }
    </div>

    @defer (on idle) {
      <app-image-viewer #viewerRef />
    }
    @defer (on idle) {
      <app-item-sheet #itemSheetRef [ordering]="canAddToCart()" />
    }
    @defer (on idle) {
      <app-info-sheet
        #infoSheetRef
        [wifi]="menu.wifi()"
        [phone]="menu.phone()"
        [address]="menu.address()"
        [socials]="menu.socials()"
      />
    }
    @if (menu.ordering()) {
      @defer (on idle) {
        <app-cart-sheet #cartSheetRef [createEnabled]="menu.orderCreate()" />
      }
    }
    @if (menu.showBill()) {
      @defer (on idle) {
        <app-orders-sheet #ordersSheetRef />
      }
    }
    @if (menu.showCallWaiter()) {
      @defer (on idle) {
        <app-waiter-sheet #waiterSheetRef />
      }
    }
    <app-toast-host />
  </div>
}
